import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  List,
  Button,
  Card,
  Row,
  Col,
  message,
  Skeleton,
  Popconfirm,
  Empty,
  Alert,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import PurchaseSummaryCard from "./PurchaseSummaryCard";
import MarketPlaceService from "../../../utils/api/marketplace";
import UserService from "../../../utils/api/users";
import { CartCache } from "../../../utils/cartCache";
import useAppStore from "../../../store/useAppStore";
import "../../../pages/Marketplace/shoppingCart/shoppingList.css";

const DEFAULT_PRODUCT_IMAGE = "/default-product.png";

const ShoppingList = ({ cart, onCartUpdate }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const isGuest = !user?.id;
  const history = useNavigate();
  const location = useLocation();
  const updateCart = useAppStore((state) => state.updateCart);
  const storeCart = useAppStore((state) => state.cart);
  const cartProducts = storeCart?.products || [];

  const [internalProducts, setInternalProducts] = useState([]);
  const [refProducts, setRefProducts] = useState([]);
  const [loadProducts, setLoadProducts] = useState(false);
  const [updatingProduct, setUpdatingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [clientAddress, setClientAddress] = useState(null);
  const [originAddress, setOriginAddress] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(false);

  const [paymentAlert, setPaymentAlert] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [skipCartFetch, setSkipCartFetch] = useState(false);

  const loadProductsInProgress = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const isMounted = useRef(true);

  const isShoppingCart = location.pathname === "/marketplace/shopping-cart";
  const freeShipping = false;

  const isClient =
    user?.role === "4" || user?.role === "client" || user?.role === "cliente";
  const isAdmin =
    user?.role === "1" ||
    user?.role === "admin" ||
    user?.role === "administrator";

  const userMemo = useMemo(() => {
    return user;
  }, [user?.id, user?.role]);

  const getItemId = useCallback((item) => item?.id ?? item?.product_id, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchClientAddress = useCallback(async () => {
    if (user?.id && isClient && !clientAddress) {
      setLoadingAddress(true);
      try {
        const response = await UserService.GetUserById({ id: user.id });
        const currentUser = response.data;

        if (currentUser && (currentUser.postal_code || currentUser.zipCode)) {
          const formattedAddress = {
            zipCode: currentUser.postal_code || currentUser.zipCode || "",
            city: currentUser.city || "",
            countryCode:
              currentUser.country === "México"
                ? "MX"
                : currentUser.country || "MX",
            addressLine: `${currentUser.street || ""} ${
              currentUser.ext_num || ""
            }`.trim(),
            state: currentUser.state || "",
            fullAddress: `${currentUser.street || ""} ${
              currentUser.ext_num || ""
            }, ${currentUser.colony || ""}, ${currentUser.city || ""}, ${
              currentUser.state || ""
            }, CP: ${currentUser.postal_code || ""}`,
            phone: currentUser.phone || "",
          };

          setClientAddress(formattedAddress);
          return formattedAddress;
        } else {
          setClientAddress(null);
          return null;
        }
      } catch (error) {
        setClientAddress(null);
        return null;
      } finally {
        setLoadingAddress(false);
      }
    }
    return clientAddress;
  }, [user?.id, isClient, clientAddress]);

  useEffect(() => {
    setOriginAddress({
      zipCode: "32470",
      city: "Juárez",
      countryCode: "MX",
      addressLine: "C. Isaac Newton 1650",
      state: "Chihuahua",
      fullAddress:
        "C. Isaac Newton 1650, Bermúdez, 32470 Juárez, Chihuahua, México",
    });
  }, []);

  const getAddressOnDemand = useCallback(async () => {
    if (clientAddress) {
      return clientAddress;
    }
    const address = await fetchClientAddress();
    return address;
  }, [fetchClientAddress, clientAddress]);

  useEffect(() => {
    if (location.state?.paymentCompleted) {
      setSkipCartFetch(true);

      const transactionInfo = JSON.parse(
        localStorage.getItem("last_transaction_info") || "{}"
      );

      setPaymentResult({
        type: "success",
        title: "¡Pago Completado Exitosamente!",
        message:
          location.state.message ||
          "Tu pedido ha sido procesado correctamente.",
        transactionId:
          location.state.transactionId || transactionInfo.transactionId,
        orderId: location.state.orderId || transactionInfo.orderId,
        amount: location.state.total || transactionInfo.amount,
      });

      setShowPaymentModal(true);
      clearCart();
      window.history.replaceState({}, document.title);

      setTimeout(() => {
        localStorage.removeItem("last_transaction_info");
        localStorage.removeItem("pending_cart_id");
        localStorage.removeItem("pending_cart_total");
        localStorage.removeItem("shipping_info");
      }, 5000);
    } else if (location.state?.paymentFailed) {
      setPaymentResult({
        type: "error",
        title: "Pago Fallido",
        message:
          location.state.error ||
          "No se pudo completar el pago. Por favor intenta nuevamente.",
        transactionId: location.state.transactionId,
      });

      setShowPaymentModal(true);
      window.history.replaceState({}, document.title);
    }

    const paymentAlertFromStorage = localStorage.getItem("payment_alert");
    if (paymentAlertFromStorage) {
      try {
        const alertData = JSON.parse(paymentAlertFromStorage);
        setPaymentAlert(alertData);
        localStorage.removeItem("payment_alert");
      } catch (error) {
        console.error("Error parseando alerta de pago:", error);
      }
    }
  }, [location.state]);

  const clearCart = useCallback(() => {
    setInternalProducts([]);
    setRefProducts([]);
    updateCart({ products: [] });

    if (!userMemo) {
      return;
    }

    const cartKey = `cart_${userMemo.id}_${userMemo.role}`;
    localStorage.removeItem(cartKey);
    localStorage.removeItem("pending_cart_id");
    localStorage.removeItem("pending_cart_total");
    localStorage.removeItem("payment_success_info");
    localStorage.removeItem("shipping_info");
    window.dispatchEvent(new Event("cartUpdated"));
    onCartUpdate?.();
  }, [userMemo, onCartUpdate, updateCart]);

  const updateCartStorage = useCallback(
    (updatedProducts, options = {}) => {
      const { notifyParent = true } = options;
      const cartKey = userMemo
        ? `cart_${userMemo.id}_${userMemo.role}`
        : "cart_guest";

      if (!updatedProducts || updatedProducts.length === 0) {
        localStorage.removeItem(cartKey);
        updateCart({ products: [] });
      } else {
        const validProducts = updatedProducts
          .filter(
            (item) =>
              item && typeof item === "object" && (item.id || item.product_id)
          )
          .map((item) => (item.id ? item : { ...item, id: item.product_id }));
        localStorage.setItem(cartKey, JSON.stringify(validProducts));
        updateCart({ products: validProducts });
      }

      window.dispatchEvent(new Event("cartUpdated"));
      if (notifyParent) {
        onCartUpdate?.();
      }
    },
    [userMemo, onCartUpdate, updateCart]
  );

  const getProductPrice = useCallback(
    (product) => {
      const productId = getItemId(product);
      const refProduct = refProducts.find((p) => getItemId(p) === productId);

      if (refProduct) {
        if (
          refProduct.newPrice !== undefined &&
          refProduct.newPrice !== null &&
          refProduct.newPrice !== 0
        ) {
          return Number(refProduct.newPrice);
        }
        return Number(refProduct.price) || 0;
      }

      if (
        product.newPrice !== undefined &&
        product.newPrice !== null &&
        product.newPrice !== 0
      ) {
        return Number(product.newPrice);
      }

      return Number(product.price) || 0;
    },
    [refProducts, getItemId]
  );

  const getUpdatedProduct = useCallback(
    (product) => {
      const productId = getItemId(product);
      const refProduct = refProducts.find((p) => getItemId(p) === productId);
      if (refProduct) {
        return {
          ...product,
          price: refProduct.price,
          newPrice: refProduct.newPrice,
          stock: refProduct.stock,
          weight_kg: refProduct.weight_kg,
          height_cm: refProduct.height_cm,
          length_cm: refProduct.length_cm,
          width_cm: refProduct.width_cm,
        };
      }
      return {
        ...product,
        weight_kg: product.weight_kg,
        height_cm: product.height_cm,
        length_cm: product.length_cm,
        width_cm: product.width_cm,
      };
    },
    [refProducts, getItemId]
  );

  useEffect(() => {
    if (skipCartFetch) {
      if (isMounted.current) setInternalProducts([]);
      return;
    }

    if (!cartProducts || cartProducts.length === 0) {
      if (internalProducts.length > 0 && isMounted.current) {
        setInternalProducts([]);
        setRefProducts([]);
        setLoadProducts(false);
      }
      return;
    }

    const cartProductsString = JSON.stringify(cartProducts);
    const internalProductsString = JSON.stringify(internalProducts);

    if (cartProductsString !== internalProductsString) {
      if (isMounted.current) {
        setInternalProducts(cartProducts);
        // Keep UI responsive: ref data is loaded in background.
        setLoadProducts(false);

        loadRefProducts(cartProducts);
      }
    }
  }, [cartProducts, skipCartFetch]);

  const loadRefProducts = useCallback(
    async (products) => {
      if (!products?.length) {
        if (isMounted.current) {
          setLoadProducts(false);
        }
        return;
      }

      if (loadProductsInProgress.current) {
        return;
      }

      const now = Date.now();
      if (
        now - lastLoadTimeRef.current < 2000 &&
        lastLoadTimeRef.current !== 0
      ) {
        if (isMounted.current) {
          setLoadProducts(false);
        }
        return;
      }

      if (skipCartFetch) {
        if (isMounted.current) setLoadProducts(false);
        return;
      }

      loadProductsInProgress.current = true;
      lastLoadTimeRef.current = now;

      try {
        const newRefProducts = [];
        const { cached, toFetch } = CartCache.getProductsBulk(
          products.map((p) => p.id)
        );

        newRefProducts.push(...cached);

        if (toFetch.length > 0) {
          const promises = toFetch.map(async (productId) => {
            try {
              const resp = await MarketPlaceService.getProductById(productId);
              if (resp.data && resp.data.item && resp.data.item[0]?.product) {
                const productData = resp.data.item[0].product;
                CartCache.setProduct(productId, productData);
                return productData;
              }
              return null;
            } catch (error) {
              console.error(`Error fetching product ${productId}:`, error);
              return null;
            }
          });

          const fetchedProducts = (await Promise.all(promises)).filter(
            (p) => p !== null
          );
          newRefProducts.push(...fetchedProducts);
        }

        if (isMounted.current) {
          setRefProducts(newRefProducts);
          setLoadProducts(false);
        }
      } catch (error) {
        console.error("Error in loadRefProducts:", error);
        if (isMounted.current) {
          setLoadProducts(false);
        }
      } finally {
        loadProductsInProgress.current = false;
      }
    },
    [skipCartFetch]
  );

  const subtotal = useMemo(() => {
    if (skipCartFetch) {
      return 0;
    }

    return isShoppingCart
      ? internalProducts.reduce((acc, item) => {
          const itemPrice = getProductPrice(item);
          const totalItem = itemPrice * (item.quantity || 1);
          return acc + totalItem;
        }, 0)
      : 0;
  }, [internalProducts, isShoppingCart, getProductPrice, skipCartFetch]);

  const handleChange = async (option, productId) => {
    const refProduct = refProducts.find((p) => getItemId(p) === productId);

    if (!refProduct) {
      message.error("Información de stock no disponible.");
      return;
    }

    setUpdatingProduct(productId);

    const updatedProducts = internalProducts.map((item) => {
      if (getItemId(item) === productId) {
        if (option === 1 && item.quantity >= refProduct.stock) {
          message.warning(
            "No puedes agregar más productos de los disponibles en stock."
          );
          return item;
        }

        const newQuantity =
          option === 1 ? item.quantity + 1 : Math.max(1, item.quantity - 1);

        return {
          ...item,
          quantity: newQuantity,
        };
      }
      return item;
    });

    setInternalProducts(updatedProducts);
    updateCartStorage(updatedProducts, { notifyParent: isGuest });

    try {
      if (!isGuest) {
        await MarketPlaceService.addProductToCart({
          client_id: user.id,
          products: updatedProducts,
        });
      }
    } catch (error) {
      console.error("Error actualizando carrito:", error);
    } finally {
      setUpdatingProduct(null);
    }
  };

  const handleDeleteProduct = async (product_id) => {
    setDeletingProduct(product_id);

    const previousProducts = [...internalProducts];

    const updatedProducts = internalProducts.filter(
      (item) => getItemId(item) !== product_id
    );

    setInternalProducts(updatedProducts);
    updateCartStorage(updatedProducts, { notifyParent: isGuest });

    try {
      if (!isGuest) {
        await MarketPlaceService.deleteCartProduct(user.id, product_id);
      }
      message.success("Producto eliminado del carrito.");
    } catch (error) {
      setInternalProducts(previousProducts);
      updateCartStorage(previousProducts);
      message.error("Error eliminando producto del carrito.");
    } finally {
      setDeletingProduct(null);
    }
  };

  const handleCardClick = (product) => {
    const productId = getItemId(product);
    history(
      `/marketplace/${
        product.category
      }/catalog/${productId}?name=${encodeURIComponent(product.name)}`
    );
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentResult(null);
    setSkipCartFetch(false);
  };

  const handleCloseAlert = () => {
    setPaymentAlert(null);
  };

  const renderPaymentModal = () => {
    if (!paymentResult) return null;

    return (
      <Modal
        open={showPaymentModal}
        onCancel={handleClosePaymentModal}
        footer={[
          <Button key="close" type="primary" onClick={handleClosePaymentModal}>
            {paymentResult.type === "success"
              ? "Continuar Comprando"
              : "Entendido"}
          </Button>,
        ]}
        centered
        closable={true}
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "24px" }}>
          {paymentResult.type === "success" ? (
            <CheckCircleOutlined
              style={{
                fontSize: "64px",
                color: "#52c41a",
                marginBottom: "16px",
              }}
            />
          ) : (
            <CloseCircleOutlined
              style={{
                fontSize: "64px",
                color: "#ff4d4f",
                marginBottom: "16px",
              }}
            />
          )}

          <h2>{paymentResult.title}</h2>
          <p style={{ margin: "16px 0", fontSize: "16px" }}>
            {paymentResult.message}
          </p>

          {paymentResult.transactionId && (
            <div
              style={{
                backgroundColor: "#f6ffed",
                padding: "12px",
                borderRadius: "6px",
                margin: "16px 0",
              }}
            >
              <p style={{ margin: 0, fontSize: "14px" }}>
                <strong>ID de Transacción:</strong>{" "}
                {paymentResult.transactionId}
              </p>
              {paymentResult.orderId && (
                <p style={{ margin: "5px 0 0 0", fontSize: "14px" }}>
                  <strong>Número de Orden:</strong> {paymentResult.orderId}
                </p>
              )}
            </div>
          )}

          {paymentResult.type === "success" && paymentResult.amount && (
            <div style={{ margin: "16px 0" }}>
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#52c41a",
                }}
              >
                Total Pagado: ${Number(paymentResult.amount).toFixed(2)} MXN
              </p>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  const ProductImage = ({ item }) => {
    const [imgError, setImgError] = useState(false);

    const imageSrc = useMemo(() => {
      if (imgError) return DEFAULT_PRODUCT_IMAGE;
      return item.imgs?.[0] || DEFAULT_PRODUCT_IMAGE;
    }, [item.imgs, imgError]);

    return (
      <div className="product-image-container">
        <img
          src={imageSrc}
          alt={item.name}
          className="product-image"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  };

  const renderProductItem = useCallback(
    (item, index) => {
      const currentItemId = getItemId(item);
      const updatedProduct = getUpdatedProduct(item);
      const itemPrice = getProductPrice(item);
      const totalPrice = itemPrice * (item.quantity || 1);
      const hasSpecialPrice =
        updatedProduct.newPrice &&
        updatedProduct.newPrice !== updatedProduct.price;
      const itemKey = `${currentItemId || "product"}-${index}`;

      return (
        <List.Item key={itemKey}>
          <Card
            className={`cart-product-card ${
              deletingProduct === currentItemId ? "deleting" : ""
            }`}
            onClick={() => handleCardClick(item)}
          >
            <Row className="product-grid" gutter={[32, 0]}>
              <Col span={6} className="product-image-col">
                <ProductImage item={item} />
              </Col>

              <Col span={10} className="product-info-col">
                <h3 className="product-name">{item.name}</h3>
                <p className="product-description">{item.description}</p>
                <div className="product-meta">
                  {isAdmin && (
                    <div className="admin-prices">
                      <span className="supplier-price">
                        Proveedor: $
                        {Number(updatedProduct.price || 0).toFixed(2)}
                      </span>
                      {updatedProduct.newPrice && (
                        <span className="client-price">
                          Cliente: $
                          {Number(updatedProduct.newPrice || 0).toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                  <span className="stock-info">
                    Stock: {updatedProduct.stock || 0}
                  </span>
                  <div className="product-dimensions">
                    {updatedProduct.weight_kg > 0 && (
                      <span className="dimension-badge">
                        Peso: {updatedProduct.weight_kg.toFixed(2)} kg
                      </span>
                    )}
                    {updatedProduct.height_cm > 0 && (
                      <span className="dimension-badge">
                        Altura: {updatedProduct.height_cm} cm
                      </span>
                    )}
                    {updatedProduct.length_cm > 0 && (
                      <span className="dimension-badge">
                        Largo: {updatedProduct.length_cm} cm
                      </span>
                    )}
                    {updatedProduct.width_cm > 0 && (
                      <span className="dimension-badge">
                        Ancho: {updatedProduct.width_cm} cm
                      </span>
                    )}
                  </div>
                </div>
              </Col>

              <Col span={8} className="product-actions-col">
                <div className="price-section">
                  {isAdmin ? (
                    <div className="admin-price-display">
                      <div className="final-price">
                        ${totalPrice.toFixed(2)}
                      </div>
                      <div className="price-breakdown">
                        {hasSpecialPrice ? (
                          <span className="price-type">
                            (Precio cliente: $
                            {Number(updatedProduct.newPrice || 0).toFixed(2)})
                          </span>
                        ) : (
                          <span className="price-type">
                            (Precio proveedor: $
                            {Number(updatedProduct.price || 0).toFixed(2)})
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="product-price">
                      ${totalPrice.toFixed(2)}
                    </div>
                  )}

                  {isAdmin && hasSpecialPrice && (
                    <div className="margin-info">
                      Margen: $
                      {(updatedProduct.newPrice - updatedProduct.price).toFixed(
                        2
                      )}
                      (
                      {updatedProduct.price > 0
                        ? (
                            ((updatedProduct.newPrice - updatedProduct.price) /
                              updatedProduct.price) *
                            100
                          ).toFixed(1)
                        : "0"}
                      %)
                    </div>
                  )}

                  <div className="shipping-badge">
                    <TruckOutlined />
                    <span>Envío por DHL (calcular costo)</span>
                  </div>
                </div>

                <div className="quantity-section">
                  <div className="quantity-controls">
                    <button
                      className={`quantity-btn ${
                        updatingProduct === currentItemId ? "loading" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange(0, currentItemId);
                      }}
                      disabled={
                        item.quantity <= 1 || updatingProduct === currentItemId
                      }
                    >
                      {updatingProduct === currentItemId ? "" : "−"}
                    </button>
                    <span className="quantity-display">{item.quantity}</span>
                    <button
                      className={`quantity-btn ${
                        updatingProduct === currentItemId ? "loading" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange(1, currentItemId);
                      }}
                      disabled={
                        item.quantity >= (updatedProduct.stock || 0) ||
                        updatingProduct === currentItemId
                      }
                    >
                      {updatingProduct === currentItemId ? "" : "+"}
                    </button>
                  </div>
                </div>

                <Popconfirm
                  title="Eliminar producto"
                  description="¿Estás seguro de que quieres eliminar este producto del carrito?"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    handleDeleteProduct(currentItemId);
                  }}
                  onCancel={(e) => e?.stopPropagation()}
                  okText="Eliminar"
                  cancelText="Cancelar"
                >
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    className="delete-btn"
                    onClick={(e) => e.stopPropagation()}
                    loading={deletingProduct === currentItemId}
                  >
                    Eliminar
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Card>
        </List.Item>
      );
    },
    [
      getUpdatedProduct,
      getProductPrice,
      isAdmin,
      updatingProduct,
      deletingProduct,
      handleChange,
      handleDeleteProduct,
      handleCardClick,
      getItemId,
    ]
  );

  const renderProductSkeleton = () => (
    <List.Item>
      <Card className="cart-product-card">
        <Row className="product-grid" gutter={[16, 16]}>
          <Col xs={24} md={6} className="product-image-col">
            <Skeleton.Image active className="product-image-skeleton" />
          </Col>
          <Col xs={24} md={10} className="product-info-col">
            <Skeleton active paragraph={{ rows: 2 }} />
          </Col>
          <Col xs={24} md={8} className="product-actions-col">
            <Skeleton active paragraph={{ rows: 3 }} />
          </Col>
        </Row>
      </Card>
    </List.Item>
  );

  if (skipCartFetch) {
    return (
      <div className="shopping-cart-container">
        {renderPaymentModal()}

        <div className="empty-cart-state">
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <CheckCircleOutlined
              style={{
                fontSize: "64px",
                color: "#52c41a",
                marginBottom: "24px",
              }}
            />
            <h2>¡Compra Completada!</h2>
            <p style={{ fontSize: "16px", marginBottom: "24px" }}>
              {paymentResult?.message ||
                "Tu pedido ha sido procesado exitosamente. Te enviaremos los detalles por correo."}
            </p>
            {paymentResult?.transactionId && (
              <p style={{ fontSize: "14px", color: "#666" }}>
                ID de transacción: {paymentResult.transactionId}
              </p>
            )}
            {paymentResult?.orderId && (
              <p style={{ fontSize: "14px", color: "#666" }}>
                Número de orden: {paymentResult.orderId}
              </p>
            )}
            <div style={{ marginTop: "32px" }}>
              <Button
                type="primary"
                size="large"
                onClick={() => history("/marketplace")}
                className="empty-cart-button"
              >
                Seguir Comprando
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadProducts && (!internalProducts || internalProducts.length === 0)) {
    return (
      <div className="shopping-cart-container">
        <div className="products-list-section">
          <List
            grid={{ gutter: 16, column: 1 }}
            dataSource={[1, 2, 3]}
            renderItem={() => renderProductSkeleton()}
          />
        </div>
      </div>
    );
  }

  const isEmptyCart = !internalProducts || internalProducts.length === 0;
  return (
    <div className="shopping-cart-container">
      {renderPaymentModal()}

      {paymentAlert && (
        <Alert
          message={paymentAlert.message}
          description={paymentAlert.description}
          type={paymentAlert.type}
          showIcon
          icon={
            paymentAlert.type === "success" ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
          style={{
            marginBottom: 16,
            borderRadius: 8,
          }}
          closable
          onClose={handleCloseAlert}
        />
      )}

      {isEmptyCart ? (
        <div className="empty-cart-state">
          <Empty
            image={<ShoppingCartOutlined className="empty-cart-icon" />}
            description={
              <div>
                <h3>Tu carrito está vacío</h3>
                <p>Agrega algunos productos para comenzar a comprar</p>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => history("/marketplace")}
                  className="empty-cart-button"
                >
                  Explorar Productos
                </Button>
              </div>
            }
          />
        </div>
      ) : (
        <div className="shopping-cart-content">
          <div className="products-list-section">
            <List
              grid={{ gutter: 16, column: 1 }}
              dataSource={internalProducts}
              renderItem={renderProductItem}
            />
          </div>

          <div className="purchase-summary-section">
            <PurchaseSummaryCard
              products={internalProducts}
              productsCount={internalProducts.length}
              subtotal={subtotal}
              freeShipping={freeShipping}
              getProductPrice={getProductPrice}
              destinationAddress={clientAddress}
              originAddress={originAddress}
              onFetchAddress={getAddressOnDemand}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ShoppingList, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.cart) === JSON.stringify(nextProps.cart);
});
